import { onBeforeUnmount, ref } from 'vue'
import { differenceInSeconds } from 'date-fns'

import { useAuthStore } from '../stores/auth'
import { useToast } from './useToast'
import {
  clearEmailVerificationCooldown,
  readEmailVerificationCooldown,
  startEmailVerificationCooldown,
} from '../utils/emailVerificationCooldown'

export function useEmailVerification() {
  const authStore = useAuthStore()
  const { triggerToast } = useToast()

  const remainingSeconds = ref(0)
  const isSending = ref(false)
  let intervalId: ReturnType<typeof setInterval> | null = null

  const clearCountdown = () => {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  const tickCooldown = (cooldownUntil: Date) => {
    const update = () => {
      const remaining = differenceInSeconds(cooldownUntil, Date.now())
      if (remaining <= 0) {
        remainingSeconds.value = 0
        clearCountdown()
        clearEmailVerificationCooldown()
        return
      }
      remainingSeconds.value = remaining
    }

    update()
    if (remainingSeconds.value > 0) {
      intervalId = setInterval(update, 1000)
    }
  }

  const startCooldown = (seconds?: number) => {
    clearCountdown()
    tickCooldown(startEmailVerificationCooldown(seconds))
  }

  const resend = async () => {
    if (remainingSeconds.value > 0 || isSending.value) return

    isSending.value = true
    try {
      await authStore.sendEmailVerification()
      startCooldown()
      triggerToast({
        message: 'Te enviamos el correo de confirmación',
        color: 'success',
      })
    } catch (error: any) {
      const responseData = error?.response?.data
      // Keeps the button in sync when the server is the one rejecting the resend
      const retryAfterSeconds =
        responseData?.data?.retryAfterSeconds || responseData?.retryAfterSeconds
      if (retryAfterSeconds) {
        startCooldown(retryAfterSeconds)
      }
    } finally {
      isSending.value = false
    }
  }

  const storedCooldown = readEmailVerificationCooldown()
  if (storedCooldown) {
    tickCooldown(storedCooldown)
  }

  onBeforeUnmount(clearCountdown)

  return {
    remainingSeconds,
    isSending,
    resend,
  }
}
